/**
 * TanStack Query hooks for Reports data fetching
 *
 * Provides hooks with caching, loading states, error handling,
 * and automatic refetching for report data.
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchReports,
  fetchReportDetails,
  shareReport,
  updateReportItemEdit,
  bulkUpdateReportItemsExcluded,
  beginProcessing,
  createReport,
  ReportSummary,
  ReportDetails,
  ShareReportPayload,
  ShareReportResponse,
  ItemEditPayload,
  ItemEditResponse,
  BulkUpdateItemsExcludedPayload,
  BulkUpdateItemsExcludedResponse,
  BeginProcessingResponse,
  CreateReportPayload,
  CreateReportResponse,
  ApiError,
} from '../services/reportsApi';
import { CACHE_TIME, HTTP_STATUS, TIMEOUT } from '../constants';
import { TimeoutError } from '../utils/fetchWithTimeout';

/**
 * Retry function for React Query.
 * Never retries timeout errors or 429s - let user decide.
 * Retries other errors up to 3 times.
 */
export const shouldRetry = (failureCount: number, error: Error): boolean => {
  if (error instanceof TimeoutError) {
    return false;
  }
  // 429 means the gateway is already shedding load; retrying amplifies the
  // burst. The user can retry by hand (parseApiError marks it retryable).
  if (error instanceof ApiError && error.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
    return false;
  }
  return failureCount < 3;
};

// Query keys for cache management
export const reportKeys = {
  all: ['reports'] as const,
  lists: () => [...reportKeys.all, 'list'] as const,
  details: () => [...reportKeys.all, 'detail'] as const,
  detail: (id: string) => [...reportKeys.details(), id] as const,
};

// Mutation keys — used with useIsMutating to detect in-flight saves.
//
// TanStack Query v5 prefix matching: a registered mutation matches a filter
// when the *registered* key starts with the *filter* key. So the filter must
// be ≤ the registered key. useUpdateReportItemEdit registers under the bare
// ['mutations', 'updateReportItem'] (no reportId at hook-mount time), so the
// modal must filter with the same bare key — which is exactly what
// `updateReportItemBase` returns.
export const mutationKeys = {
  all: ['mutations'] as const,
  updateReportItemBase: ['mutations', 'updateReportItem'] as const,
  bulkUpdateReportItems: ['mutations', 'updateReportItem', 'bulk'] as const,
};

/**
 * Hook to fetch all reports for the current user.
 * Data is cached for 30 seconds and refetched on window focus.
 */
export function useReports() {
  return useQuery<ReportSummary[], Error>({
    queryKey: reportKeys.lists(),
    queryFn: fetchReports,
    staleTime: CACHE_TIME.STALE_TIME_MS,
    refetchOnWindowFocus: true,
    retry: shouldRetry,
  });
}

/**
 * Hook to fetch single report details.
 * Only fetches when reportId is provided.
 */
export function useReportDetails(reportId: string | undefined) {
  return useQuery<ReportDetails, Error>({
    queryKey: reportKeys.detail(reportId || ''),
    queryFn: () => fetchReportDetails(reportId as string), // Safe: enabled ensures reportId exists
    enabled: !!reportId,
    staleTime: CACHE_TIME.STALE_TIME_MS,
    retry: shouldRetry,
  });
}

/**
 * Why status polling gave up before the report settled.
 * `'error'`: the last poll failed with an error polling cannot fix (see
 * isFatalPollError). `'timeout'`: STATUS_POLL_DEADLINE_MS passed first.
 */
export type StatusPollStopReason = 'error' | 'timeout';

/**
 * An error no amount of polling fixes: any 4xx except 408 (request timeout)
 * and 429 (throttle). A 401 is an expired session, a 404 a report that is not
 * there; asking again every 5 seconds changes neither. 5xx, timeouts and
 * network failures may clear, so they keep polling until the deadline.
 */
export function isFatalPollError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  const { status } = error;
  return (
    status >= 400 &&
    status < 500 &&
    status !== HTTP_STATUS.REQUEST_TIMEOUT &&
    status !== HTTP_STATUS.TOO_MANY_REQUESTS
  );
}

/**
 * Hook to poll for report status after upload.
 *
 * Polls every 5 seconds until the report is `completed` or `failed`, and also
 * stops, reporting why in `stopReason`, when:
 * - the last poll failed with a fatal error (isFatalPollError);
 * - STATUS_POLL_DEADLINE_MS has passed since polling started.
 * A 429 slows the poll to its Retry-After, or STATUS_POLL_THROTTLED_INTERVAL_MS
 * without one, rather than stopping it.
 *
 * The deadline is per report id: once it passes, re-enabling for the same id
 * stays stopped. Uses the shorter polling timeout for each request.
 */
export function useReportStatus(reportId: string | undefined, enabled: boolean) {
  const active = enabled && !!reportId;
  // The id whose deadline has passed. Keyed by id so a new upload in the same
  // mounted form starts with a fresh window and no reset step.
  const [expiredId, setExpiredId] = useState<string | null>(null);
  const timedOut = active && expiredId === reportId;

  useEffect(() => {
    if (!active || !reportId) {
      return undefined;
    }
    const timer = setTimeout(() => setExpiredId(reportId), CACHE_TIME.STATUS_POLL_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [active, reportId]);

  const query = useQuery<ReportDetails, Error>({
    queryKey: reportKeys.detail(reportId || ''),
    queryFn: () => fetchReportDetails(reportId as string, TIMEOUT.POLLING_MS), // Safe: enabled ensures reportId exists
    // Disabled, not just a false refetchInterval, so nothing (a focus refetch
    // included) keeps this observer fetching past the deadline.
    enabled: active && !timedOut,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (status === 'completed' || status === 'failed') {
        return false;
      }
      const { error } = q.state;
      if (isFatalPollError(error)) {
        return false;
      }
      if (error instanceof ApiError && error.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
        // Floored at the normal cadence so a Retry-After of 0 cannot speed it up.
        return Math.max(
          error.retryAfterMs ?? CACHE_TIME.STATUS_POLL_THROTTLED_INTERVAL_MS,
          CACHE_TIME.POLL_INTERVAL_MS,
        );
      }
      return CACHE_TIME.POLL_INTERVAL_MS;
    },
    staleTime: 0, // Always refetch for status polling
    retry: shouldRetry,
  });

  let stopReason: StatusPollStopReason | null = null;
  if (timedOut) {
    stopReason = 'timeout';
  } else if (isFatalPollError(query.error)) {
    stopReason = 'error';
  }

  return { ...query, stopReason };
}

/**
 * Mutation hook for signalling upload completion.
 * The frontend calls this after every PDF for a report has finished uploading
 * to S3 — the API kicks off processing. Invalidates the detail cache so the
 * subsequent status poll picks up the new state.
 */
export function useBeginProcessing() {
  const queryClient = useQueryClient();
  return useMutation<
    BeginProcessingResponse,
    Error,
    { reportId: string; expectedFileCount: number }
  >({
    mutationFn: ({ reportId, expectedFileCount }) =>
      beginProcessing(reportId, expectedFileCount),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
    },
  });
}

/**
 * Mutation hook for sharing a report via email.
 * Invalidates the report detail cache on success.
 */
export function useShareReport() {
  const queryClient = useQueryClient();
  return useMutation<
    ShareReportResponse,
    Error,
    { reportId: string; payload: ShareReportPayload }
  >({
    mutationFn: ({ reportId, payload }) => shareReport(reportId, payload),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
    },
  });
}

/**
 * Mutation hook for persisting per-item user edits.
 * Invalidates the report detail cache on success so hydrated state reflects
 * the saved edit on next read.
 */
export function useUpdateReportItemEdit() {
  const queryClient = useQueryClient();
  return useMutation<
    ItemEditResponse,
    Error,
    { reportId: string; itemId: string; edit: ItemEditPayload }
  >({
    mutationKey: ['mutations', 'updateReportItem'],
    mutationFn: ({ reportId, itemId, edit }) =>
      updateReportItemEdit(reportId, itemId, edit),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
    },
  });
}

/**
 * Bulk-set `excluded` on a list of report items in one API call.
 * Replaces the per-item PATCH loops on Select-All-Visible /
 * Deselect-All-Visible / Deselect-All controls.
 *
 * Registered under ['mutations', 'updateReportItem', 'bulk'] so the existing
 * `useIsMutating({ mutationKey: updateReportItemBase })` filter in
 * ShareReportModal also gates on this mutation (TanStack v5 prefix matching).
 */
export function useBulkUpdateReportItemsExcluded() {
  const queryClient = useQueryClient();
  return useMutation<
    BulkUpdateItemsExcludedResponse,
    Error,
    { reportId: string; payload: BulkUpdateItemsExcludedPayload }
  >({
    mutationKey: mutationKeys.bulkUpdateReportItems,
    mutationFn: ({ reportId, payload }) =>
      bulkUpdateReportItemsExcluded(reportId, payload),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
    },
  });
}

/**
 * Mutation hook for creating a Reports row server-side BEFORE PDF upload.
 *
 * On success, invalidates the reports list so MyReports immediately shows
 * the new pending row. Does NOT invalidate the detail cache — the row has
 * no items at this point, and `useReportStatus()` is the right consumer of
 * detail-cache data once polling starts.
 */
export function useCreateReport() {
  const queryClient = useQueryClient();
  return useMutation<
    CreateReportResponse,
    Error,
    CreateReportPayload
  >({
    mutationFn: (payload) => createReport(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
    },
  });
}
